import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid, Button, TextField, MenuItem, CircularProgress, Box, Paper, IconButton, InputAdornment, Popper, ClickAwayListener } from '@mui/material';
import { useFormik } from 'formik';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
	safeDateParse,
	formatDateForInput,
	formatDateForPayload,
	formatDateMMDDYYYY,
	getTodayISO,
	formatDatePickerForInput,
} from 'shared/utils/dateUtils';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import {
	fetchAllEventTypes,
	postEvent,
	updateEvent,
	deleteEvent,
	fetchSingleEvent,
	postStaffing,
	fetchAllJobs,
	fetchAllLocations,
	getEventDocumentByReference,
	deleteEventDocumentByReference,
} from 'core/API/events';
import { getJobCodes } from 'core/API/scheduler';
import styles from './CreateEvent.module.scss';
import { fetchAllCustomers } from 'core/API/customers';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BanquetEventOrderTemplateFill from 'module/events/components/BanquetEventOrderTemplateFill';

interface IJobCodeItem {
	id: string;
	jobCodeId: number | null;
	numberOfAdditionalStaff: number;
}

interface IEvent {
	eventName: string;
	startDate: string;
	startTime: Dayjs | null;
	endDate: string;
	endTime: Dayjs | null;
	eventType: number;
	numOfAtendees: number;
	location: number;
	organizersName: number;
	organizersEmail: string;
	organizersPhoneNumber: string;
	jobCodes: IJobCodeItem[];
	taxExemptId: string;
	note: string;
	/** Path from file/upload — same as schedule Add Event modal */
	banquet_event_order: string;
}

const CreateEvent: React.FC = () => {
	const history = useHistory();
	const location = useLocation();
	const { workplaceId, timezone } = useWorkplacesContext();
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const [staffing, setStaffing] = useState<any[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isJobSelected, setIsJobSelected] = useState(false);
	const [startCalendarOpen, setStartCalendarOpen] = useState(false);
	const [endCalendarOpen, setEndCalendarOpen] = useState(false);
	const [documentLinks, setDocumentLinks] = useState<Array<{ ref: string; url: string; label: string }>>([]);
	const [documentsLoading, setDocumentsLoading] = useState(false);
	const startCalendarAnchorRef = useRef<HTMLDivElement>(null);
	const endCalendarAnchorRef = useRef<HTMLDivElement>(null);

	const locationState = location.state as any;
	const isEditMode = !!locationState?.eventId || !!locationState?.editEvent;
	const editEventData = locationState?.editEvent || null;
	const resolveEventId = (value: any): number | null => {
		if (value === null || value === undefined) return null;
		if (typeof value === 'number') return Number.isFinite(value) ? value : null;
		if (typeof value === 'string') {
			const match = value.match(/(\d+)(?:\/)?$/);
			if (!match) return null;
			const parsed = Number(match[1]);
			return Number.isFinite(parsed) ? parsed : null;
		}
		if (typeof value === 'object') {
			if (value.id !== undefined && value.id !== null) return resolveEventId(value.id);
			if (value['@id']) return resolveEventId(value['@id']);
		}
		return null;
	};
	const eventId =
		resolveEventId(locationState?.eventId) ||
		resolveEventId(editEventData?.id) ||
		resolveEventId(editEventData?.['@id']) ||
		null;
	
	const { data: fetchedEvent, isLoading: isLoadingEvent } = useQuery(
		['event', eventId],
		() => fetchSingleEvent(eventId),
		{
			enabled: isEditMode && !!eventId,
			initialData: editEventData || undefined,
			staleTime: 0,
			refetchOnMount: 'always',
		}
	);
	
	const eventData = fetchedEvent || editEventData;
	
	const extractIdFromIri = (iri: string | null | undefined): number => {
		if (!iri || typeof iri !== 'string') return 0;
		const id = parseInt(iri.split('/').pop() || '0');
		return isNaN(id) ? 0 : id;
	};
	
	const eventTypeId = eventData?.type ? extractIdFromIri(eventData.type) : (locationState?.resource || 0);
	const locationId = eventData?.location ? extractIdFromIri(eventData.location) : 0;
	const customerId = eventData?.customer ? extractIdFromIri(eventData.customer) : 0;
	
	const getCustomerEmailAndPhone = (customerId: number, customersList: any) => {
		if (!customerId || !customersList?.member) return { email: '', phone: '' };
		const customer = customersList.member.find((c: any) => c.id === customerId);
		return {
			email: customer?.email || '',
			phone: customer?.phone || '',
		};
	};
	
	const getInitialJobCodesFromEvent = (): IJobCodeItem[] => {
		if (eventData?.eventStaffing && Array.isArray(eventData.eventStaffing) && eventData.eventStaffing.length > 0) {
			return eventData.eventStaffing.map((staffing: any, index: number) => ({
				id: `jobcode-${Date.now()}-${index}-${Math.random()}`,
				jobCodeId: staffing.jobCode ? extractIdFromIri(staffing.jobCode) : null,
				numberOfAdditionalStaff: staffing.numberOfAdditionalStaff || 0,
			}));
		}
		return [];
	};

	const toDocumentRef = (item: any): string => {
		if (!item) return '';
		if (typeof item === 'string') return item.trim();
		if (typeof item === 'object') {
			return String(item['@id'] || item.filePath || item.file_path || item.path || '').trim();
		}
		return '';
	};

	const normalizeDocumentsToCsv = (raw: any): string => {
		if (!raw) return '';
		if (Array.isArray(raw)) {
			return raw
				.map((item) => toDocumentRef(item))
				.filter(Boolean)
				.join(',');
		}
		if (typeof raw === 'string') {
			return raw
				.split(',')
				.map((item) => item.trim())
				.filter(Boolean)
				.join(',');
		}
		return '';
	};

	const normalizeDocumentsToList = (raw: any): string[] => {
		if (!raw) return [];
		if (Array.isArray(raw)) return raw.map((x) => toDocumentRef(x)).filter(Boolean);
		if (typeof raw === 'string') {
			return raw
				.split(',')
				.map((x) => x.trim())
				.filter(Boolean);
		}
		return [];
	};

	const { data: eventTypes, isFetching: isFetchingEventTypes } = useQuery(
		['eventTypes', workplaceId],
		() => fetchAllEventTypes(workplaceId),
		{
			enabled: !!workplaceId,
			refetchOnMount: 'always',
		}
	);

	const { data: customers, isFetching: isFetchingCustomers } = useQuery(
		['customers'],
		() => fetchAllCustomers(),
		{
			staleTime: Infinity,
		}
	);

	const { data: jobs, isFetching: isFetchingJobs } = useQuery(
		['jobs'],
		() => fetchAllJobs(),
		{
			staleTime: Infinity,
		}
	);

	const { data: jobCodesData, isLoading: isLoadingJobCodes } = useQuery(
		['jobCodes'],
		() => getJobCodes(),
		{
			staleTime: Infinity,
		}
	);

	// Sort job codes by job number
	const sortedJobCodesData = useMemo(() => {
		if (!jobCodesData) return [];
		return [...jobCodesData].sort((a, b) => {
			const aNum = a.jobNumber ?? 999;
			const bNum = b.jobNumber ?? 999;
			return aNum - bNum;
		});
	}, [jobCodesData]);

	const { data: locations, isFetching: isFetchinglocation } = useQuery(
		['locations', workplaceId],
		() => fetchAllLocations(workplaceId),
		{
			enabled: !!workplaceId,
			refetchOnMount: 'always',
		}
	);

	const { mutateAsync: addStaffing } = useMutation(postStaffing);

	const refsToDeleteOnSave = useRef<string[]>([]);

	const { mutate: createEvent, isLoading: isCreatingEvent } = useMutation(postEvent, {
		onSuccess: async (data) => {
			for (const staff of staffing) {
				const sendObj = {
					event: {
						id: data.id,
					},
					role: {
						id: staff.role.id,
					},
					needed_number_of_staff: staff.needed_number_of_staff,
				};
				await addStaffing(sendObj);
			}
			for (const ref of refsToDeleteOnSave.current) {
				try { await deleteEventDocumentByReference(ref); } catch { /* best-effort */ }
			}
			refsToDeleteOnSave.current = [];
			showToast('success', 'Event successfully created!');
			queryClient.invalidateQueries(['events']);
			queryClient.invalidateQueries(['events-month']);
			queryClient.invalidateQueries(['events-month-all']);
			history.push('/manager/events');
		},
		onError: () => {
			showToast('error', 'Failed to create event');
			setIsSubmitting(false);
		},
	});

	const { mutate: updateEventMutation, isLoading: isUpdatingEvent } = useMutation(updateEvent, {
		onSuccess: async () => {
			for (const ref of refsToDeleteOnSave.current) {
				try { await deleteEventDocumentByReference(ref); } catch { /* best-effort */ }
			}
			refsToDeleteOnSave.current = [];
			showToast('success', 'Event successfully updated!');
			queryClient.invalidateQueries(['events']);
			queryClient.invalidateQueries(['events-month']);
			queryClient.invalidateQueries(['events-month-all']);
			history.push('/manager/events');
		},
		onError: () => {
			showToast('error', 'Failed to update event');
			setIsSubmitting(false);
		},
	});

	const { mutate: deleteEventMutation, isLoading: isDeletingEvent } = useMutation(deleteEvent, {
		onSuccess: () => {
			showToast('success', 'Event successfully deleted!');
			queryClient.invalidateQueries(['events']);
			queryClient.invalidateQueries(['events-month']);
			queryClient.invalidateQueries(['events-month-all']);
			history.push('/manager/events');
		},
		onError: () => {
			showToast('error', 'Failed to delete event');
		},
	});

	// Helper to extract only date part (YYYY-MM-DD) from datetime string
	const extractDateOnly = (dateString: string | null | undefined): string => {
		if (!dateString) return '';
		const dateMatch = String(dateString).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
		if (dateMatch) {
			const year = dateMatch[1];
			const month = dateMatch[2].padStart(2, '0');
			const day = dateMatch[3].padStart(2, '0');
			return `${year}-${month}-${day}`;
		}
		return String(dateString);
	};

	const formatDateForDisplay = (dateString: string): string => {
		if (!dateString) return '';
		
		// Extract date part from datetime string if needed (e.g., "2025-12-02T04:37:21" -> "2025-12-02")
		const dateOnly = extractDateOnly(dateString);
		const [year, month, day] = dateOnly.split('-');
		if (year && month && day) {
			// Convert YYYY-MM-DD to MM/DD/YYYY format directly without Date object
			return `${month}/${day}/${year}`;
		}
		
		// Fallback to standard formatter
		return formatDateMMDDYYYY(dateString);
	};

	// Helper function to extract only date part from datetime string
	// No timezone conversion - just extract the date as-is from the string

	// Helper function to convert UTC date to workplace timezone and extract time as Dayjs
	const parseTimeFromUTC = (utcDateString: string | null | undefined): Dayjs | null => {
		if (!utcDateString) return null;
		
		// Parse the UTC date string and convert to workplace timezone
		const utcMoment = momentTZ.utc(utcDateString);
		const localMoment = utcMoment.tz(timezone);
		
		// Extract time and convert to Dayjs
		return dayjs()
			.set('hour', localMoment.hour())
			.set('minute', localMoment.minute())
			.set('second', localMoment.second())
			.set('millisecond', 0);
	};

	const handleStartCalendarChange = (val: Date | null) => {
		if (val) {
			const newISO = formatDatePickerForInput(val);
			formik.setFieldValue('startDate', newISO);
			
			if (formik.values.endDate) {
				const currentEndDate = safeDateParse(extractDateOnly(formik.values.endDate));
				if (currentEndDate) {
					const normalizedEndDate = new Date(currentEndDate);
					normalizedEndDate.setHours(0, 0, 0, 0);
					const normalizedVal = new Date(val);
					normalizedVal.setHours(0, 0, 0, 0);
					
					if (normalizedEndDate < normalizedVal) {
						formik.setFieldValue('endDate', newISO);
						if (formik.values.startTime && formik.values.endTime) {
							if (formik.values.endTime.isBefore(formik.values.startTime) || formik.values.endTime.isSame(formik.values.startTime)) {
								formik.setFieldValue('endTime', formik.values.startTime.add(1, 'hour'));
							}
						}
					}
				}
			}
		}
		setStartCalendarOpen(false);
	};

	const handleEndCalendarChange = (val: Date | null) => {
		if (val) {
			if (formik.values.startDate) {
				const startDate = safeDateParse(extractDateOnly(formik.values.startDate));
				
				if (startDate) {
					const normalizedStartDate = new Date(startDate);
					normalizedStartDate.setHours(0, 0, 0, 0);
					const normalizedVal = new Date(val);
					normalizedVal.setHours(0, 0, 0, 0);
					
					if (normalizedVal < normalizedStartDate) {
						showToast('error', 'End date cannot be before start date');
						setEndCalendarOpen(false);
						return;
					}
					
					if (normalizedVal.getTime() === normalizedStartDate.getTime()) {
						if (formik.values.startTime && formik.values.endTime) {
							if (formik.values.endTime.isBefore(formik.values.startTime) || formik.values.endTime.isSame(formik.values.startTime)) {
								formik.setFieldValue('endTime', formik.values.startTime.add(1, 'hour'));
							}
						}
					}
				}
			}
			
			const newISO = formatDatePickerForInput(val);
			formik.setFieldValue('endDate', newISO);
		}
		setEndCalendarOpen(false);
	};

	const parseDateTimeToDayjs = (value?: string | Date | null): Dayjs | null => {
		if (!value) return null;
		const date = value instanceof Date ? value : new Date(value);
		if (isNaN(date.getTime())) {
			return null;
		}
		return dayjs(date);
	};

	const parseTimeStringToDayjs = (value?: string | null): Dayjs | null => {
		if (!value) return null;
		const [hoursStr, minutesStr, secondsStr] = value.split(':');
		const hours = Number(hoursStr);
		const minutes = Number(minutesStr ?? '0');
		const seconds = Number(secondsStr ?? '0');

		if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
			return null;
		}

		return dayjs()
			.set('hour', hours)
			.set('minute', minutes)
			.set('second', seconds)
			.set('millisecond', 0);
	};

	const getDefaultStartTimeDayjs = (): Dayjs | null => {
		// Priority 1: Use startTime field directly (separate time field)
		if (locationState?.startTime) {
			const fromLocationState = parseTimeStringToDayjs(locationState.startTime);
			if (fromLocationState) {
				return fromLocationState;
			}
		}
		
		// Priority 2: Use eventData.startTime field
		const fromStartTime = parseTimeStringToDayjs(eventData?.startTime);
		if (fromStartTime) {
			return fromStartTime;
		}

		// Priority 3: Use locationState date (for new events)
		if (locationState?.date) {
			const dateFromState = parseDateTimeToDayjs(locationState.date);
			if (dateFromState) {
				return dateFromState;
			}
		}

		// Priority 4: Fallback to extracting time from startDate/start (only if startTime is not available)
		if (eventData?.startDate && !eventData?.startTime) {
			const timeFromUTC = parseTimeFromUTC(eventData.startDate);
			if (timeFromUTC) return timeFromUTC;
		}
		
		if (eventData?.start && !eventData?.startTime) {
			const timeFromUTC = parseTimeFromUTC(eventData.start);
			if (timeFromUTC) return timeFromUTC;
		}

		return dayjs().set('hour', 9).set('minute', 0).set('second', 0).set('millisecond', 0);
	};

	const getDefaultEndTimeDayjs = (): Dayjs | null => {
		// Priority 1: Use endTime field directly (separate time field)
		if (locationState?.endTime) {
			const fromLocationState = parseTimeStringToDayjs(locationState.endTime);
			if (fromLocationState) {
				return fromLocationState;
			}
		}
		
		// Priority 2: Use eventData.endTime field
		const fromEndTime = parseTimeStringToDayjs(eventData?.endTime);
		if (fromEndTime) {
			return fromEndTime;
		}

		// Priority 3: Use locationState date and startTime (for new events)
		if (locationState?.date && locationState?.startTime) {
			const startTime = parseTimeStringToDayjs(locationState.startTime);
			if (startTime) {
				return startTime.add(1, 'hour');
			}
		}

		// Priority 4: Fallback to extracting time from endDate/end (only if endTime is not available)
		if (eventData?.endDate && !eventData?.endTime) {
			const timeFromUTC = parseTimeFromUTC(eventData.endDate);
			if (timeFromUTC) return timeFromUTC;
		}
		
		if (eventData?.end && !eventData?.endTime) {
			const timeFromUTC = parseTimeFromUTC(eventData.end);
			if (timeFromUTC) return timeFromUTC;
		}

		return dayjs().set('hour', 10).set('minute', 0).set('second', 0).set('millisecond', 0);
	};

	const initialFormValues = useMemo<IEvent>(() => {
		// Simple date extraction from API response - just get YYYY-MM-DD from datetime string
		const getDateOnly = (dateStr: string | null | undefined): string => {
			if (!dateStr) return getTodayISO();
			const match = String(dateStr).match(/^(\d{4}-\d{2}-\d{2})/);
			return match ? match[1] : getTodayISO();
		};
		
		const startDateValue = eventData?.startDate 
			? getDateOnly(eventData.startDate)
			: (eventData?.start 
				? getDateOnly(eventData.start) 
				: (locationState?.date 
					? formatDateForInput(locationState.date) 
					: getTodayISO()));
		
		const endDateValue = eventData?.endDate 
			? getDateOnly(eventData.endDate)
			: (eventData?.end 
				? getDateOnly(eventData.end) 
				: (locationState?.date 
					? formatDateForInput(locationState.date) 
					: getTodayISO()));
		
		return {
			eventName: eventData?.name || '',
			startDate: startDateValue,
			startTime: getDefaultStartTimeDayjs(),
			endDate: endDateValue,
			endTime: getDefaultEndTimeDayjs(),
			eventType: eventTypeId,
			numOfAtendees: eventData?.numberOfAttendees || eventData?.size || 1,
			location: locationId,
			organizersName: customerId || 0,
			organizersEmail: eventData?.organizer_email || (customerId && customers?.member ? getCustomerEmailAndPhone(customerId, customers).email : ''),
			organizersPhoneNumber: eventData?.organizer_phone_number || (customerId && customers?.member ? getCustomerEmailAndPhone(customerId, customers).phone : ''),
			jobCodes: getInitialJobCodesFromEvent(),
			taxExemptId: eventData?.taxExemptId || (customerId && customers?.member ? customers.member.find((c: any) => c.id === customerId)?.taxExemptId || '' : ''),
			note: eventData?.notes || eventData?.note || '',
			banquet_event_order:
				normalizeDocumentsToCsv((eventData as any)?.documents) ||
				normalizeDocumentsToCsv((eventData as any)?.banquet_event_order),
		};
	}, [eventData, eventTypeId, locationId, customerId, customers?.member, locationState]);

	useEffect(() => {
		let cancelled = false;
		const uploadsBase = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
		const refs = normalizeDocumentsToList((eventData as any)?.documents || (eventData as any)?.banquet_event_order);

		const resolveOne = async (ref: string) => {
			if (/^https?:\/\//i.test(ref)) {
				return { ref, url: ref, label: ref.split('/').pop() || 'Open PDF' };
			}
			if (ref.startsWith('/api/')) {
				try {
					const doc = await getEventDocumentByReference(ref);
					const raw = doc.filePath || doc.file_path || doc.path || '';
					if (!raw) return { ref, url: '', label: 'Preview unavailable' };
					const clean = String(raw).replace(/^\/+/, '').replace(/^uploads\//, '');
					const url = `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`;
					const label = doc.fileName || doc.file_name || clean.split('/').pop() || 'Open PDF';
					return { ref, url, label };
				} catch {
					return { ref, url: '', label: 'Preview unavailable' };
				}
			}
			// raw file name from API documents[].filePath
			if (!ref.includes('/') && ref.toLowerCase().endsWith('.pdf')) {
				const clean = ref.replace(/^\/+/, '');
				return {
					ref,
					url: `${uploadsBase}/event_documents/${clean}`,
					label: clean,
				};
			}
			const clean = ref.replace(/^\/+/, '').replace(/^uploads\//, '');
			return {
				ref,
				url: `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`,
				label: clean.split('/').pop() || 'Open PDF',
			};
		};

		const resolveAll = async () => {
			if (!refs.length) {
				if (!cancelled) setDocumentLinks([]);
				return;
			}
			setDocumentsLoading(true);
			const out = await Promise.all(refs.map((r) => resolveOne(r)));
			if (!cancelled) {
				setDocumentLinks(out);
				setDocumentsLoading(false);
			}
		};

		resolveAll();
		return () => {
			cancelled = true;
		};
	}, [eventData]);

	const timePickerSlotProps = useMemo(() => ({
		textField: {
			fullWidth: true,
			className: styles.formInput,
			sx: {
				'& .MuiOutlinedInput-root': {
					borderRadius: '6px',
					fontSize: '0.95rem',
					backgroundColor: '#fff',
					color: '#3c5471',
					fontFamily: 'Gotham',
					'& fieldset': {
						border: '1px solid rgba(0,0,0,0.23)',
					},
					'&:hover fieldset': {
						border: '1px solid rgba(0,0,0,0.23)',
					},
					'&.Mui-focused fieldset': {
						border: '1px solid rgba(0,0,0,0.23)',
					},
				},
				'& input': {
					fontFamily: 'Gotham',
					color: '#3c5471',
				},
				'& label': {
					fontFamily: 'Gotham',
					color: '#3c5471',
				},
			},
		},
		desktopPaper: {
			sx: {
				'.MuiMultiSectionDigitalClock-root': {
					'--MultiSectionDigitalClockSection-itemWidth': '64px',
				},
				'.MuiMultiSectionDigitalClockSection-root': {
					width: '64px',
				},
				'.MuiMultiSectionDigitalClockSection-item': {
					color: 'rgb(68, 99, 136)',
					fontFamily: 'Gotham',
				},
				'.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
					backgroundColor: 'rgb(68, 99, 136)',
					color: '#fff',
				},
				'.MuiMultiSectionDigitalClockSection-item:hover': {
					backgroundColor: 'rgb(68, 99, 136)',
					color: '#fff',
				},
			},
		},
		mobilePaper: {
			sx: {
				'.MuiMultiSectionDigitalClockSection-root': {
					width: '64px',
				},
				'.MuiMultiSectionDigitalClockSection-item': {
					color: 'rgb(68, 99, 136)',
					fontFamily: 'Gotham',
				},
				'.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
					backgroundColor: 'rgb(68, 99, 136)',
					color: '#fff',
				},
				'.MuiMultiSectionDigitalClockSection-item:hover': {
					backgroundColor: 'rgb(68, 99, 136)',
					color: '#fff',
				},
			},
		},
	}), []);

	const validateEventForm = (values: IEvent) => {
		const errors: Partial<Record<keyof IEvent, string>> = {};

		if (!values.eventType || Number(values.eventType) === 0) {
			errors.eventType = 'Event type is required';
		}

		if (!values.location || Number(values.location) === 0) {
			errors.location = 'Location is required';
		}

		return errors;
	};

	const formik = useFormik<IEvent>({
		enableReinitialize: true,
		initialValues: initialFormValues,
		validate: validateEventForm,
		onSubmit: (data) => {
			setIsSubmitting(true);
			
			const startDateStr = formatDateForPayload(data.startDate);
			const startTimeStr = data.startTime ? data.startTime.format('HH:mm:ss') : '00:00:00';
			// Parse the date/time string directly in the specified timezone to preserve the selected date
			const startDateTime = moment.tz(`${startDateStr} ${startTimeStr}`, 'MM/DD/YYYY HH:mm:ss', timezone);
			const newStart_at = startDateTime.utc().toDate().toISOString();

			const endDateStr = formatDateForPayload(data.endDate);
			const endTimeStr = data.endTime ? data.endTime.format('HH:mm:ss') : '00:00:00';
			// Parse the date/time string directly in the specified timezone to preserve the selected date
			const endDateTime = moment.tz(`${endDateStr} ${endTimeStr}`, 'MM/DD/YYYY HH:mm:ss', timezone);
			const newEnd_at = endDateTime.utc().toDate().toISOString();

			const customerIri = data.organizersName
				? `/api/customer_lists/${data.organizersName}`
				: null;
			const documents = (data.banquet_event_order || '')
				.split(',')
				.map((item) => item.trim())
				.filter(Boolean);

			const eventPayload = {
				workplace: "/api/workplaces/"+workplaceId,
				name: data?.eventName,
				startDate: newStart_at.substring(0, newStart_at.length - 5),
				startTime: startTimeStr,
				endDate: newEnd_at.substring(0, newEnd_at.length - 5),
				endTime: endTimeStr,
				numberOfAttendees: data?.numOfAtendees,
				notes: data?.note,
				type: "/api/settings_event_types/"+Number.parseInt(`${data?.eventType}`),
				location: "/api/settings_locations/"+data?.location,
				taxExemptId: data.taxExemptId,
				customer: customerIri,
				documents,
				eventStaffing: (data.jobCodes || []).map((jc: IJobCodeItem) => ({
					jobCode: jc.jobCodeId ? "/api/job_codes/"+jc.jobCodeId : null,
					numberOfAdditionalStaff: jc.numberOfAdditionalStaff || 0
				})).filter((staff: any) => staff.jobCode !== null)
			};

			if (isEditMode && eventId) {
				updateEventMutation({
					id: eventId,
					body: eventPayload,
				});
			} else {
				createEvent(eventPayload);
			}
		},
	});

	const handleDelete = () => {
		if (window.confirm('Are you sure you want to delete this event?')) {
			if (eventId) {
				deleteEventMutation(eventId);
			}
		}
	};

	const handleCustomerChange = (e) => {
		handleChange(e);

		const customerIdValue = Number(e.target.value);
		
		if (customerIdValue === 0) {
			formik.setFieldValue("organizersEmail", "");
			formik.setFieldValue("organizersPhoneNumber", "");
			formik.setFieldValue("taxExemptId", "");
			return;
		}

		const selected = customers?.member?.find(
			(c) => c.id === customerIdValue
		);

		if (selected) {
			formik.setFieldValue("organizersEmail", selected.email || "");
			formik.setFieldValue("organizersPhoneNumber", selected.phone || "");
			formik.setFieldValue("taxExemptId", selected.taxExemptId || "");
		}
	};

	useEffect(() => {
		if (customerId && customers?.member && Array.isArray(customers.member)) {
			const selectedCustomer = customers.member.find((c: any) => c.id === customerId);
			if (selectedCustomer) {
				if (selectedCustomer.email) {
					formik.setFieldValue("organizersEmail", selectedCustomer.email);
				}
				if (selectedCustomer.phone) {
					formik.setFieldValue("organizersPhoneNumber", selectedCustomer.phone);
				}
				if (selectedCustomer.taxExemptId) {
					formik.setFieldValue("taxExemptId", selectedCustomer.taxExemptId);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerId, customers]);

	const getInitialJobCodes = (): IJobCodeItem[] => {
		return [];
	};

	const addJobCode = (e?: React.MouseEvent) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		const currentJobCodes = Array.isArray(formik.values.jobCodes) ? formik.values.jobCodes : [];
		const availableJobCodes = sortedJobCodesData || [];
		
		const selectedIds = currentJobCodes.map(jc => jc.jobCodeId).filter(Boolean);
		const unselectedJobCode = availableJobCodes.find(jc => !selectedIds.includes(jc.id));
		
		if (unselectedJobCode) {
			const newJobCode: IJobCodeItem = {
				id: `jobcode-${Date.now()}-${Math.random()}`,
				jobCodeId: unselectedJobCode.id,
				numberOfAdditionalStaff: 0,
			};
			formik.setFieldValue('jobCodes', [...currentJobCodes, newJobCode]);
		}
	};

	const removeJobCode = (id: string) => {
		const currentJobCodes = Array.isArray(formik.values.jobCodes) ? formik.values.jobCodes : [];
		const updatedJobCodes = currentJobCodes.filter((jc) => jc.id !== id);
		formik.setFieldValue('jobCodes', updatedJobCodes);
	};

	const handleJobCodeChange = (id: string, jobCodeId: number | null) => {
		const currentJobCodes = Array.isArray(formik.values.jobCodes) ? formik.values.jobCodes : [];
		const updatedJobCodes = currentJobCodes.map(jc => 
			jc.id === id ? { ...jc, jobCodeId } : jc
		);
		formik.setFieldValue('jobCodes', updatedJobCodes);
	};

	const handleNumberOfAdditionalStaffChange = (id: string, value: number) => {
		const currentJobCodes = Array.isArray(formik.values.jobCodes) ? formik.values.jobCodes : [];
		const updatedJobCodes = currentJobCodes.map(jc => 
			jc.id === id ? { ...jc, numberOfAdditionalStaff: value } : jc
		);
		formik.setFieldValue('jobCodes', updatedJobCodes);
	};

	const allJobCodesSelected = () => {
		const currentJobCodes = Array.isArray(formik.values.jobCodes) ? formik.values.jobCodes : [];
		const availableJobCodes = sortedJobCodesData || [];
		const selectedIds = currentJobCodes.map(jc => jc.jobCodeId).filter(Boolean);
		return selectedIds.length >= availableJobCodes.length;
	};

	const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

	if (isFetchingEventTypes || (isEditMode && isLoadingEvent && !editEventData)) {
		return (
			<Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
			<Box maxWidth="1100px" mx="auto">
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Button
						onClick={() => history.push('/manager/events')}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>

					<Box display="flex" gap={2}>
						<Button
							type="submit"
							variant="contained"
							form="createEventForm"
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: 4,
								textTransform: 'none',
								'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
							}}
							disabled={isSubmitting || isCreatingEvent || isUpdatingEvent}
						>
							{isSubmitting || isCreatingEvent || isUpdatingEvent ? 'Saving...' : 'Save'}
						</Button>
					</Box>
				</Box>

				<Paper elevation={1}>
					<Box sx={{ p: 3 }}>
						<h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '24px', color: '#333', marginTop: 0 }}>
							{isEditMode ? 'Edit Event' : 'Create Event'}
						</h2>

						<form id="createEventForm" onSubmit={handleSubmit}>
							<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>
							Event Name <span className={styles.required}>*</span>
						</label>
						<TextField
							name="eventName"
							className={styles.formInput}
							required
							fullWidth
							value={values.eventName}
							onChange={(e) => {
								const value = e.target.value;
								if (value.length <= 25) {
									handleChange(e);
								}
							}}
							onBlur={handleBlur}
							error={touched.eventName && Boolean(errors.eventName)}
							helperText={
								touched.eventName && errors.eventName
									? errors.eventName
									: `${values.eventName.length}/25 characters`
							}
							placeholder="Name event"
							inputProps={{ maxLength: 25 }}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>
							Event Type <span className={styles.required}>*</span>
						</label>
						<TextField
							name="eventType"
							select
							className={styles.formInput}
							required
							fullWidth
							value={values.eventType}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.eventType && Boolean(errors.eventType)}
							helperText={touched.eventType && errors.eventType}
						>
							<MenuItem value={0}>Choose event type</MenuItem>
							{eventTypes?.member?.map((type: any) => (
								<MenuItem key={type.id} value={type.id}>
									{type.label}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					<Grid item xs={12} sm={6} ref={startCalendarAnchorRef}>
						<label className={styles.formLabel}>
							Start Date <span className={styles.required}>*</span>
						</label>
						<TextField
							name="startDate"
							fullWidth
							required
							value={values.startDate ? formatDateForDisplay(values.startDate) : ''}
							InputLabelProps={{ shrink: true }}
							error={touched.startDate && Boolean(errors.startDate)}
							helperText={touched.startDate && errors.startDate}
							InputProps={{
								readOnly: true,
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => setStartCalendarOpen(true)}
											size="small"
											sx={{
												color: '#3c5471',
												'&:hover': {
													backgroundColor: 'rgba(0,0,0,0.04)',
												},
											}}
										>
											<CalendarTodayIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							}}
							sx={{
								'& .MuiOutlinedInput-root': {
									borderRadius: '6px',
									fontSize: '0.95rem',
									backgroundColor: '#fff',
									color: '#3c5471',
									fontFamily: 'Gotham',
									'& fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
									'&:hover fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
									'&.Mui-focused fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
								},
								'& input': {
									fontFamily: 'Gotham',
									color: '#3c5471',
									cursor: 'pointer',
								},
							}}
							onClick={() => setStartCalendarOpen(true)}
						/>

						<Popper
							open={startCalendarOpen}
							anchorEl={startCalendarAnchorRef.current}
							placement="top-start"
							style={{
								zIndex: 999999999999,
								position: 'fixed',
								top: '20px',
								left: '300px',
							}}
							modifiers={[
								{
									name: 'offset',
									options: {
										offset: [0, -8],
									},
								},
								{
									name: 'preventOverflow',
									options: {
										boundary: 'viewport',
										altBoundary: true,
									},
								},
								{
									name: 'flip',
									options: {
										fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
									},
								},
								{
									name: 'computeStyles',
									options: {
										gpuAcceleration: false,
									},
								},
							]}
						>
							<ClickAwayListener onClickAway={() => setStartCalendarOpen(false)}>
								<Box
									sx={{
										p: 0,
										mb: 1,
										borderRadius: '8px',
										overflow: 'hidden',
										backgroundColor: 'white',
										boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
										position: 'relative',
										'& .react-datepicker': {
											fontFamily: 'Gotham',
											border: 'none',
											borderRadius: '8px',
											fontSize: '14px',
											width: '290px',
											position: 'relative',
										},
										'& .react-datepicker__header': {
											backgroundColor: '#f8f9fa',
											borderBottom: '1px solid #e9ecef',
											borderRadius: '8px 8px 0 0',
											padding: '12px 0',
										},
										'& .react-datepicker__current-month': {
											color: '#3c5471',
											fontWeight: 'bold',
											fontSize: '16px',
											margin: '8px 0',
										},
										'& .react-datepicker__day-name': {
											color: '#6c757d',
											fontWeight: '600',
											width: '36px',
											height: '36px',
											lineHeight: '36px',
											margin: '2px',
											fontSize: '13px',
										},
										'& .react-datepicker__day': {
											color: '#3c5471',
											borderRadius: '4px',
											margin: '2px',
											width: '36px',
											height: '36px',
											lineHeight: '36px',
											fontSize: '14px',
											'&:hover': {
												backgroundColor: '#e3f2fd',
											},
											'&.react-datepicker__day--selected': {
												backgroundColor: '#2196f3',
												color: 'white',
												fontWeight: 'bold',
												'&:hover': {
													backgroundColor: '#1976d2',
												},
											},
											'&.react-datepicker__day--keyboard-selected': {
												backgroundColor: '#2196f3',
												color: 'white',
											},
											'&.react-datepicker__day--disabled': {
												color: '#ccc',
												cursor: 'not-allowed',
											},
											'&.react-datepicker__day--outside-month': {
												color: '#ccc',
											},
											'&.react-datepicker__day--today': {
												backgroundColor: '#e8f5e8',
												color: '#2e7d32',
												fontWeight: 'bold',
											},
										},
										'& .react-datepicker__navigation': {
											top: '12px',
											'& .react-datepicker__navigation-icon': {
												borderColor: '#3c5471',
												'&:hover': {
													borderColor: '#1976d2',
												},
											},
										},
										'& .react-datepicker__month-select, & .react-datepicker__year-select': {
											border: '1px solid #ddd',
											borderRadius: '4px',
											padding: '4px 8px',
											margin: '0 4px',
											fontSize: '14px',
											backgroundColor: 'white',
										},
										'& .react-datepicker__month-container': {
											backgroundColor: 'white',
										},
										'& .react-datepicker__month': {
											margin: '8px',
										},
										'& .react-datepicker__triangle': {
											display: 'none',
										},
									}}
								>
									<ReactDatePicker
										inline
										selected={values.startDate ? safeDateParse(extractDateOnly(values.startDate)) : null}
										onChange={handleStartCalendarChange}
										showYearDropdown
										scrollableYearDropdown
										yearDropdownItemNumber={100}
										dateFormat="MM/dd/yyyy"
										showMonthDropdown
										dropdownMode="select"
										calendarStartDay={1}
										shouldCloseOnSelect={true}
									/>
								</Box>
							</ClickAwayListener>
						</Popper>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>
							Start Time <span className={styles.required}>*</span>
						</label>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								value={values.startTime}
								onChange={(newTime) => {
									formik.setFieldValue('startTime', newTime);
									
									if (values.startDate && values.endDate) {
										const startDate = safeDateParse(extractDateOnly(values.startDate));
										const endDate = safeDateParse(extractDateOnly(values.endDate));
										if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
											if (newTime && values.endTime && (values.endTime.isBefore(newTime) || values.endTime.isSame(newTime))) {
												formik.setFieldValue('endTime', newTime.add(1, 'hour'));
											}
										}
									}
								}}
								slotProps={timePickerSlotProps}
							/>
						</LocalizationProvider>
					</Grid>

					<Grid item xs={12} sm={6} ref={endCalendarAnchorRef}>
						<label className={styles.formLabel}>
							End Date <span className={styles.required}>*</span>
						</label>
						<TextField
							name="endDate"
							fullWidth
							required
							value={values.endDate ? formatDateForDisplay(values.endDate) : ''}
							InputLabelProps={{ shrink: true }}
							error={touched.endDate && Boolean(errors.endDate)}
							helperText={touched.endDate && errors.endDate}
							InputProps={{
								readOnly: true,
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => setEndCalendarOpen(true)}
											size="small"
											sx={{
												color: '#3c5471',
												'&:hover': {
													backgroundColor: 'rgba(0,0,0,0.04)',
												},
											}}
										>
											<CalendarTodayIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							}}
							sx={{
								'& .MuiOutlinedInput-root': {
									borderRadius: '6px',
									fontSize: '0.95rem',
									backgroundColor: '#fff',
									color: '#3c5471',
									fontFamily: 'Gotham',
									'& fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
									'&:hover fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
									'&.Mui-focused fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
								},
								'& input': {
									fontFamily: 'Gotham',
									color: '#3c5471',
									cursor: 'pointer',
								},
							}}
							onClick={() => setEndCalendarOpen(true)}
						/>

						<Popper
							open={endCalendarOpen}
							anchorEl={endCalendarAnchorRef.current}
							placement="top-start"
							style={{
								zIndex: 999999999999,
								position: 'fixed',
								top: '20px',
								left: '300px',
							}}
							modifiers={[
								{
									name: 'offset',
									options: {
										offset: [0, -8],
									},
								},
								{
									name: 'preventOverflow',
									options: {
										boundary: 'viewport',
										altBoundary: true,
									},
								},
								{
									name: 'flip',
									options: {
										fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
									},
								},
								{
									name: 'computeStyles',
									options: {
										gpuAcceleration: false,
									},
								},
							]}
						>
							<ClickAwayListener onClickAway={() => setEndCalendarOpen(false)}>
								<Box
									sx={{
										p: 0,
										mb: 1,
										borderRadius: '8px',
										overflow: 'hidden',
										backgroundColor: 'white',
										boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
										position: 'relative',
										'& .react-datepicker': {
											fontFamily: 'Gotham',
											border: 'none',
											borderRadius: '8px',
											fontSize: '14px',
											width: '290px',
											position: 'relative',
										},
										'& .react-datepicker__header': {
											backgroundColor: '#f8f9fa',
											borderBottom: '1px solid #e9ecef',
											borderRadius: '8px 8px 0 0',
											padding: '12px 0',
										},
										'& .react-datepicker__current-month': {
											color: '#3c5471',
											fontWeight: 'bold',
											fontSize: '16px',
											margin: '8px 0',
										},
										'& .react-datepicker__day-name': {
											color: '#6c757d',
											fontWeight: '600',
											width: '36px',
											height: '36px',
											lineHeight: '36px',
											margin: '2px',
											fontSize: '13px',
										},
										'& .react-datepicker__day': {
											color: '#3c5471',
											borderRadius: '4px',
											margin: '2px',
											width: '36px',
											height: '36px',
											lineHeight: '36px',
											fontSize: '14px',
											'&:hover': {
												backgroundColor: '#e3f2fd',
											},
											'&.react-datepicker__day--selected': {
												backgroundColor: '#2196f3',
												color: 'white',
												fontWeight: 'bold',
												'&:hover': {
													backgroundColor: '#1976d2',
												},
											},
											'&.react-datepicker__day--keyboard-selected': {
												backgroundColor: '#2196f3',
												color: 'white',
											},
											'&.react-datepicker__day--disabled': {
												color: '#ccc',
												cursor: 'not-allowed',
											},
											'&.react-datepicker__day--outside-month': {
												color: '#ccc',
											},
											'&.react-datepicker__day--today': {
												backgroundColor: '#e8f5e8',
												color: '#2e7d32',
												fontWeight: 'bold',
											},
										},
										'& .react-datepicker__navigation': {
											top: '12px',
											'& .react-datepicker__navigation-icon': {
												borderColor: '#3c5471',
												'&:hover': {
													borderColor: '#1976d2',
												},
											},
										},
										'& .react-datepicker__month-select, & .react-datepicker__year-select': {
											border: '1px solid #ddd',
											borderRadius: '4px',
											padding: '4px 8px',
											margin: '0 4px',
											fontSize: '14px',
											backgroundColor: 'white',
										},
										'& .react-datepicker__month-container': {
											backgroundColor: 'white',
										},
										'& .react-datepicker__month': {
											margin: '8px',
										},
										'& .react-datepicker__triangle': {
											display: 'none',
										},
									}}
								>
									<ReactDatePicker
										inline
										selected={values.endDate ? safeDateParse(extractDateOnly(values.endDate)) : null}
										onChange={handleEndCalendarChange}
										minDate={values.startDate ? safeDateParse(extractDateOnly(values.startDate)) : new Date()}
										showYearDropdown
										scrollableYearDropdown
										yearDropdownItemNumber={100}
										dateFormat="MM/dd/yyyy"
										showMonthDropdown
										dropdownMode="select"
										calendarStartDay={1}
										shouldCloseOnSelect={true}
									/>
								</Box>
							</ClickAwayListener>
						</Popper>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>
							End Time <span className={styles.required}>*</span>
						</label>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								value={values.endTime}
								shouldDisableTime={(timeValue, view) => {
									if (values.startDate && values.endDate && values.startTime) {
										const startDate = safeDateParse(extractDateOnly(values.startDate));
										const endDate = safeDateParse(extractDateOnly(values.endDate));
										if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
											if (view === 'hours' || view === 'minutes') {
												if (timeValue && (timeValue.isBefore(values.startTime) || timeValue.isSame(values.startTime))) {
													return true;
												}
											}
										}
									}
									return false;
								}}
								onChange={(newTime) => {
									if (values.startDate && values.endDate && values.startTime) {
										const startDate = safeDateParse(extractDateOnly(values.startDate));
										const endDate = safeDateParse(extractDateOnly(values.endDate));
										if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
											if (newTime && (newTime.isBefore(values.startTime) || newTime.isSame(values.startTime))) {
												showToast('error', 'End time must be after start time');
												return;
											}
										}
									}
									formik.setFieldValue('endTime', newTime);
								}}
								slotProps={timePickerSlotProps}
							/>
						</LocalizationProvider>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>
							Number of Attendees <span className={styles.required}>*</span>
						</label>
						<TextField
							name="numOfAtendees"
							type="number"
							className={styles.formInput}
							required
							fullWidth
							value={values.numOfAtendees}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.numOfAtendees && Boolean(errors.numOfAtendees)}
							helperText={touched.numOfAtendees && errors.numOfAtendees}
							inputProps={{ min: 1 }}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>
							Location <span className={styles.required}>*</span>
						</label>
						<TextField
							name="location"
							select
							className={styles.formInput}
							fullWidth
							value={values.location}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.location && Boolean(errors.location)}
							helperText={touched.location && errors.location}
						>
							<MenuItem value={0}>Choose Location</MenuItem>
							{locations?.member?.map((type: any) => (
								<MenuItem key={type.id} value={type.id}>
									{type.label}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>Customer Name</label>
						<TextField
							name="organizersName"
							select
							className={styles.formInput}
							fullWidth
							value={values.organizersName}
							onChange={handleCustomerChange}
							onBlur={handleBlur}
							error={touched.organizersName && Boolean(errors.organizersName)}
							helperText={touched.organizersName && errors.organizersName}
						>
							<MenuItem value={0}>None</MenuItem>
							{customers?.member
								?.slice()
								.sort((a: any, b: any) => {
									const nameA = (a.fullName || (a.firstName && a.lastName ? `${a.firstName} ${a.lastName}` : 'Unknown')).toLowerCase();
									const nameB = (b.fullName || (b.firstName && b.lastName ? `${b.firstName} ${b.lastName}` : 'Unknown')).toLowerCase();
									return nameA.localeCompare(nameB);
								})
								.map(function(type: any){
									const displayName = type.fullName || (type.firstName && type.lastName ? `${type.firstName} ${type.lastName}` : 'Unknown');
									return (
									<MenuItem key={type.id} value={type.id}>
										{displayName}
									</MenuItem>
								)
								})}
						</TextField>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>Customer Email</label>
						<TextField
							name="organizersEmail"
							type="email"
							disabled
							className={styles.formInput}
							fullWidth
							value={values.organizersEmail}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.organizersEmail && Boolean(errors.organizersEmail)}
							helperText={touched.organizersEmail && errors.organizersEmail}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>Customer Phone Number</label>
						<TextField
							name="organizersPhoneNumber"
							disabled
							className={styles.formInput}
							fullWidth
							value={values.organizersPhoneNumber}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.organizersPhoneNumber && Boolean(errors.organizersPhoneNumber)}
							helperText={touched.organizersPhoneNumber && errors.organizersPhoneNumber}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<label className={styles.formLabel}>Tax Exempt ID</label>
						<TextField
							name="taxExemptId"
							className={styles.formInput}
							fullWidth
							value={values.taxExemptId}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.taxExemptId && Boolean(errors.taxExemptId)}
							helperText={touched.taxExemptId && errors.taxExemptId}
						/>
					</Grid>

					<Grid item xs={12} sm={12}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
							<label className={styles.formLabel} style={{margin: 0}}>
								Job Roles to Support Event
							</label>
							<Button
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									addJobCode(e);
								}}
								disabled={allJobCodesSelected() || isLoadingJobCodes || !jobCodesData || jobCodesData.length === 0}
								style={{color: "#1976D2", border: "1px solid #1976D2"}}
								startIcon={<AddIcon />}
								variant="outlined"
								size="small"
								type="button"
							>
								Add Staff to Support Event
							</Button>
						</Box>
						{isLoadingJobCodes ? (
							<Box sx={{ py: 2, textAlign: 'center' }}>
								<CircularProgress size={24} />
							</Box>
						) : Array.isArray(formik.values.jobCodes) && formik.values.jobCodes.length > 0 ? (
							formik.values.jobCodes.map((jobCodeItem, index) => {
								const selectedIds = formik.values.jobCodes
									.filter((jc, idx) => idx !== index)
									.map(jc => jc.jobCodeId)
									.filter(Boolean);
								
								const availableJobCodes = (sortedJobCodesData || []).filter(
									jc => !selectedIds.includes(jc.id) || jc.id === jobCodeItem.jobCodeId
								);

								return (
									<Box key={jobCodeItem.id} sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
										<Box sx={{ flex: 1 }}>
											<TextField
												label="Job Code"
												select
												className={styles.formInput}
												fullWidth
												value={jobCodeItem.jobCodeId || ''}
												onChange={(e) => handleJobCodeChange(jobCodeItem.id, Number(e.target.value) || null)}
												error={!jobCodeItem.jobCodeId}
											>
												<MenuItem value="">Choose Job Code</MenuItem>
												{availableJobCodes.map((jobCode: any) => (
													<MenuItem key={jobCode.id} value={jobCode.id}>
														{jobCode.label || jobCode.name}
													</MenuItem>
												))}
											</TextField>
										</Box>
										<Box sx={{ flex: 1 }}>
											<TextField
												label="Number Of Additional Staff"
												type="number"
												className={styles.formInput}
												fullWidth
												value={jobCodeItem.numberOfAdditionalStaff}
												onChange={(e) => handleNumberOfAdditionalStaffChange(jobCodeItem.id, Number(e.target.value) || 0)}
												inputProps={{ min: 0 }}
											/>
										</Box>
										<IconButton
											onClick={() => removeJobCode(jobCodeItem.id)}
											color="error"
											size="small"
											title="Delete job code"
											sx={{ mt: 1 }}
										>
											<DeleteIcon />
										</IconButton>
									</Box>
								);
							})
						) : null}
					</Grid>

					<Grid item xs={12}>
						<BanquetEventOrderTemplateFill
							setFieldValue={formik.setFieldValue}
							value={values.banquet_event_order}
							workplaceId={workplaceId}
							labelClassName={styles.formLabel}
							onRefRemoved={(ref) => refsToDeleteOnSave.current.push(ref)}
						/>
					</Grid>

					<Grid item xs={12}>
						<label className={styles.formLabel}>Notes</label>
						<TextField
							name="note"
							multiline
							rows={4}
							className={styles.formInput}
							fullWidth
							value={values.note}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.note && Boolean(errors.note)}
							helperText={touched.note && errors.note}
						/>
					</Grid>
						</Grid>
						</form>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
};

export default CreateEvent;